import React, { useState, useMemo } from "react";
import { Dropdown, Space } from "antd";
import { useNavigate } from "react-router-dom";
import { MoreOutlined } from "@ant-design/icons";
import WidgetWrapper from "../../components/WidgetWrapper";
import { useAuth } from "../../../../hooks/useAuth";

/**
 * QuickAccessWidget - 快速入口组件
 *
 * 支持按钮级别的权限控制：
 * - 每个 item 可配置 `permission` 属性（字符串或字符串数组）
 * - 如果配置了 permission，只有用户拥有该权限时才显示该按钮
 * - 如果未配置 permission，则所有用户可见
 *
 * @param {Array} items - 入口按钮配置数组
 * @param {string} items[].label - 按钮标签
 * @param {string} items[].path - 跳转路径
 * @param {ReactNode} items[].icon - 图标
 * @param {boolean} items[].primary - 是否为主要按钮（优先展示）
 * @param {string|string[]} items[].permission - 所需权限（可选）
 */
const QuickAccessWidget = ({ items = [], title, viewMorePath, ...props }) => {
    const navigate = useNavigate();
    const { hasPermission, hasAnyPermission } = useAuth();
    const containerRef = React.useRef(null);
    const [visibleCount, setVisibleCount] = useState(items.length);

    // 根据权限过滤可见的入口项
    const permissionFilteredItems = useMemo(() => {
        return items.filter((item) => {
            // 如果没有配置权限要求，默认可见
            if (!item.permission) {
                return true;
            }
            // 如果是数组，检查是否有其中任意一个权限
            if (Array.isArray(item.permission)) {
                return hasAnyPermission(item.permission);
            }
            // 否则检查单个权限
            return hasPermission(item.permission);
        });
    }, [items, hasPermission, hasAnyPermission]);

    // Filter items by primary/secondary
    const primaryItems = permissionFilteredItems.filter((item) => item.primary);
    const secondaryItems = permissionFilteredItems.filter((item) => !item.primary);

    // Combine for display calculation: Primary always first
    const allDisplayItems = [...primaryItems, ...secondaryItems];

    const handleNavigate = (path) => {
        navigate(path);
    };

    // Responsive Logic: Calculate how many items fit
    React.useLayoutEffect(() => {
        const calculateVisibleItems = () => {
            if (!containerRef.current) return;

            const { width, height } = containerRef.current.getBoundingClientRect();
            // Target tile size (approximate)
            const ITEM_MIN_WIDTH = 160;
            const ITEM_MIN_HEIGHT = 120;
            const GAP = 2;

            // Calculate columns and rows
            const cols = Math.floor((width + GAP) / (ITEM_MIN_WIDTH + GAP));
            const rows = Math.floor((height + GAP) / (ITEM_MIN_HEIGHT + GAP));

            // Total slots available
            let maxSlots = Math.max(1, cols * rows);

            // If we have more items than slots, we need 1 slot for "More"
            if (allDisplayItems.length > maxSlots) {
                setVisibleCount(maxSlots - 1);
            } else {
                setVisibleCount(allDisplayItems.length);
            }
        };

        calculateVisibleItems();

        const observer = new ResizeObserver(calculateVisibleItems);
        if (containerRef.current) observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, [allDisplayItems.length]);

    const visibleItems = allDisplayItems.slice(0, visibleCount);
    const hiddenItems = allDisplayItems.slice(visibleCount);

    const moreMenu = {
        items: hiddenItems.map((item, index) => ({
            key: index,
            label: (
                <Space>
                    {item.icon}
                    <span>{item.label}</span>
                </Space>
            ),
            onClick: () => handleNavigate(item.path),
        })),
    };

    // Custom Tile Component
    const Tile = ({ icon, label, onClick, isMore, color = "#1890ff" }) => {
        const [hover, setHover] = useState(false);

        const baseStyle = {
            flex: "1 1 0", // Auto grow
            minWidth: "100px", // Minimum width
            height: "100%", // Fill row height
            padding: "12px 8px",
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            gap: "12px",
            background: isMore ? "#f5f5f5" : "#fff",
            // Best Practice: Use transparent border for layout stability, color it on hover
            border: hover ? "1px solid #1890ff" : "1px solid transparent",
            boxShadow: hover ? "0 4px 12px rgba(0,0,0,0.1)" : "0 2px 6px rgba(0,0,0,0.02)",
            transform: hover ? "translateY(-2px)" : "none",
        };

        return (
            <div style={baseStyle} onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
                <div style={{ fontSize: "48px", color: color, marginBottom: "4px" }}>{icon}</div>
                <div
                    style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        lineHeight: "1.3",
                        textAlign: "center",
                        color: "var(--text-primary)",
                        width: "100%",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                    }}
                >
                    {label}
                </div>
            </div>
        );
    };

    return (
        <WidgetWrapper title={title} bordered={false} {...props}>
            <div
                ref={containerRef}
                style={{
                    height: "100%",
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                    gridAutoRows: "1fr",
                    gap: "12px",
                    padding: "4px",
                }}
            >
                {visibleItems.map((item, index) => (
                    <Tile key={index} icon={item.icon} label={item.label} onClick={() => handleNavigate(item.path)} />
                ))}

                {hiddenItems.length > 0 && (
                    <Dropdown menu={moreMenu} trigger={["click", "hover"]} placement="bottomRight">
                        <div>
                            <Tile icon={<MoreOutlined />} label="更多" color="#8c8c8c" isMore={true} />
                        </div>
                    </Dropdown>
                )}
            </div>
        </WidgetWrapper>
    );
};

export default QuickAccessWidget;
